import { closeDialog } from "./dialogManager.js";
import { loadThemeBasedOnSwitch, changeTheme } from "../../../../../app/utils/theme.js";
import { currentTable } from "../../../../../app/viewManager.js";
import { exportTable, getExportRowIds } from "../../../app-table/utils/exportTable.js";
import { logoutURL } from "../../../../../app/apiClient.js";
import { selectDialogDropdowns } from "../dropdowns/dropdownManager.js";
import { formatDateDE } from "../../../app-table/utils/timeUtils.js";

import { updateTime, deleteTime } from "../../../app-time/utils/timeManager.js";
import { deleteEntry, markFactored } from "../../../app-table/tableManager/entryManager.js";
import { createProject, updateProject, deleteProject } from "../../../app-table/tableManager/projectManager.js";
import { createApplication, updateApplication, deleteApplication } from "../../../app-table/tableManager/applicationManager.js";
import { createTeam, updateTeam, deleteTeam } from "../../../app-table/tableManager/teamManager.js";
import { createUser, editUser, deleteUser } from "../../../app-table/tableManager/userManager.js";
import { deleteCustomer } from "../../../app-table/tableManager/customerManager.js";
import { createService, updateService, deleteService } from "../../../app-table/tableManager/serviceManager.js";
import { createRole, updateRole, deleteRole } from "../../../app-table/tableManager/roleManager.js";

import { currentContext } from "./dialogContext.js";
import { onCreate, onEdit, onDelete, chainSetup } from "./dialogHandlerUtils.js";
import { getContent } from "../../../app-table/tableManager/tableManager.js";
import { setupRoleDropdown, setupTeamDropdown, setupProjectsDropdown, setupPermissionsDropdown, setupActivitiesDropdown, setupMembersDropdown, setupEmploymentTypeDropdown } from "../dropdowns/dropdownInits.js";
import { buildCreateEntryHandlers, buildEditEntryHandlers } from "../entries/entryDialogHandlers.js";
import { buildCreateTimeLogHandlers, buildEditTimeLogHandlers } from "../timelogs/timeLogDialogHandlers.js";
import { deleteTimeLog } from "../../../app-table/tableManager/timeLogManager.js";
import { buildCreateCustomerHandlers } from "../customer/customerWizardHandlers.js";
import {
    buildEditCustomerHandlers,
    buildEditCustomerDashboardModulesHandlers,
    buildEditCustomerPermittedLanguagesHandlers,
    buildEditCustomerApplicationsHandlers,
    buildEditCustomerServicesHandlers,
    buildEditServiceConfigurationHandlers,
} from "../customer/customerDialogHandlers.js";

const deleteActions = {
    deleteTime:        deleteTime,
    deleteEntry:       deleteEntry,
    deleteTimeLog:     deleteTimeLog,
    deleteProject:     deleteProject,
    deleteApplication: deleteApplication,
    deleteTeam:        deleteTeam,
    deleteUser:        deleteUser,
    deleteCustomer:    deleteCustomer,
    deleteRole:        deleteRole,
    deleteService:     deleteService,
};

const deletePlaceholders = {
    deleteTime:        row => ({ start: row.start, end: row.end }),
    deleteEntry:       row => ({ customer: row.customerName, project: row.projectName, date: formatDateDE(row.date) }),
    deleteTimeLog:     row => ({ member: row.memberName, date: formatDateDE(row.date) }),
    deleteProject:     row => ({ name: row.name }),
    deleteApplication: row => ({ name: row.name }),
    deleteTeam:        row => ({ name: row.name }),
    deleteUser:        row => ({ name: row.name, email: row.email }),
    deleteCustomer:    row => ({ name: row.name }),
    deleteRole:        row => ({ name: row.name }),
    deleteService:     row => ({ name: row.name }),
};

function getDeletePlaceholders(dialogName, data) {
    const getValues = deletePlaceholders[dialogName];
    if (!getValues || !data) return {};
    if (Array.isArray(data) && data.length !== 1) {
        return { count: data.length };
    };
    const values = getValues(Array.isArray(data) ? data[0] : data);
    return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== ""));
}

async function loadSettingsDialog() {
    const root = document.querySelector("app-dialog")?.shadowRoot;
    if (!root) return;
    loadThemeBasedOnSwitch(root.querySelector("#darkModeCheck"));
}

const crudEntities = {
    Time:        { update: updateTime },
    Project:     { create: createProject,     update: updateProject },
    Application: { create: createApplication, update: updateApplication, init: { initProjectsDropdown: setupProjectsDropdown } },
    Service:     { create: createService,     update: updateService },
    Team:        { create: createTeam,        update: updateTeam,        init: { initPermissions: setupPermissionsDropdown, initActivities: setupActivitiesDropdown } },
    Role:        { create: createRole,        update: updateRole,        init: { initPermissions: setupPermissionsDropdown } },
    User:        { create: createUser, update: editUser, stripOnCreate: ["email"],
    init:        { initRoleDropdown: setupRoleDropdown, initTeamDropdown: setupTeamDropdown, initEmploymentTypeDropdown: setupEmploymentTypeDropdown } },
};

function buildCrudHandlers({ create, update, init = {}, stripOnCreate = [] }, isEdit, data) {
    const handlers = {};
    for (const [key, setupFn] of Object.entries(init)) {
        handlers[key] = (element) => chainSetup(handlers, setupFn(element, data));
    }
    const setupData = !isEdit && data
        ? Object.fromEntries(Object.entries(data).filter(([key]) => !stripOnCreate.includes(key)))
        : data;
    handlers.onSave = isEdit ? onEdit(update) : onCreate(create);
    handlers.setup  = setupData ? () => selectDialogDropdowns(setupData) : undefined;
    return handlers;
}

export async function buildHandlers(dialogName, data = null, isTask = false) {
    if (dialogName in deleteActions) {
        currentContext.id = Array.isArray(data) ? data.map(row => row.id) : data?.id;
        return {
            onDelete: onDelete(deleteActions[dialogName]),
            placeholders: getDeletePlaceholders(dialogName, data),
        };
    }

    const crudMatch = /^(create|edit)([A-Za-z]+)$/.exec(dialogName);
    const crudEntity = crudMatch && crudEntities[crudMatch[2]];
    if (crudEntity) {
        const isEdit = crudMatch[1] === "edit";
        if (isEdit) currentContext.id = data?.id;
        return buildCrudHandlers(crudEntity, isEdit, data);
    }

    switch (dialogName) {
        case "createEntry":
            return buildCreateEntryHandlers(data, isTask);
        case "editEntry":
            return buildEditEntryHandlers(data);

        case "createTimeLog":
            return buildCreateTimeLogHandlers();
        case "editTimeLog":
            return buildEditTimeLogHandlers(data);

        case "createCustomer":
            return buildCreateCustomerHandlers(data);
        case "editCustomer":
            return buildEditCustomerHandlers(data);
        case "editCustomerDashboardModules":
            return buildEditCustomerDashboardModulesHandlers(data);
        case "editCustomerPermittedLanguages":
            return buildEditCustomerPermittedLanguagesHandlers(data);
        case "editCustomerApplications":
            return buildEditCustomerApplicationsHandlers(data);
        case "editCustomerServices":
            return buildEditCustomerServicesHandlers(data);
        case "editServiceConfiguration":
            return buildEditServiceConfigurationHandlers(data);

        case "restartDay":
            return {
                onContinue: () => { data?.resolve?.('continue'); closeDialog(); },
                onNewDay:   () => { data?.resolve?.('newDay');   closeDialog(); },
            };

        case "export":
            return { onExport: async () => {
                const shouldMarkFactored = currentTable.tableName === "entry"
                    && getContent()?.querySelector("#checkboxMarkFactored")?.checked;

                if (shouldMarkFactored) {
                    const ids = getExportRowIds(currentTable);
                    if (ids.length) await markFactored(ids);
                }

                exportTable(currentTable, currentTable.tableName);
                closeDialog();
            }};

        case "settings":
            return {
                onTheme: () => changeTheme("darkModeCheck"),
                setup: async () => loadSettingsDialog()
            };

        case "user":
            return { onLogout: () => location.href = logoutURL };

        default: return {};
    }
}